import type { AvatarProps } from "@hopper-ui/components";
import { Avatar, Button, Divider, Menu, MenuItem, MenuTrigger, Text } from "@hopper-ui/components";
import { ExitIcon, KeyIcon, UserIcon } from "@hopper-ui/icons";
import { Skeleton } from "@sg-protect/components";
import { navigateTo } from "@sg-protect/core";
import config from "@sg-protect/runtime-config";
import { useLegacyHomePaths } from "@sg-protect/shell";
import { Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useSession } from "./SessionProvider.tsx";

function handleLogout() {
    navigateTo(config.get().insights.logout);
}

export function UserMenu() {
    const { account, subscription } = useLegacyHomePaths();

    const avatarProps: CurrentUserAvatarProps = {
        size: "md",
        width: "core_320",
        height: "core_320"
    };

    return (
        <MenuTrigger>
            <Button variant="ghost-secondary" aria-label="Open menu" data-telemetry="User menu">
                <ErrorBoundary fallback={<ErroredUserAvatar {...avatarProps} />}>
                    <Suspense fallback={<LoadingUserAvatar {...avatarProps} />}>
                        <CurrentUserAvatar {...avatarProps} />
                    </Suspense>
                </ErrorBoundary>
            </Button>

            <Menu size="xs" data-public>
                <MenuItem href={account}>
                    <UserIcon />
                    <Text>My account</Text>
                </MenuItem>

                <MenuItem href={subscription}>
                    <KeyIcon />
                    <Text>Subscription</Text>
                </MenuItem>

                <MenuItem onAction={handleLogout}>
                    <ExitIcon />
                    <Text>Log out</Text>
                </MenuItem>

                <Divider />

                <MenuItem href="https://sharegate.com/terms" target="_blank" rel="noopener noreferrer">
                    <Text>Terms & Conditions</Text>
                </MenuItem>
            </Menu>
        </MenuTrigger>
    );
}

interface CurrentUserAvatarProps {
    size?: AvatarProps["size"];
    width?: AvatarProps["width"];
    height?: AvatarProps["height"];
}

function CurrentUserAvatar(props: CurrentUserAvatarProps) {
    const { displayName } = useSession();

    return <Avatar name={displayName} {...props} />;
}

function LoadingUserAvatar(props: CurrentUserAvatarProps) {
    return <Skeleton borderRadius="circle" {...props} backgroundColor="neutral-disabled" />;
}

function ErroredUserAvatar(props: CurrentUserAvatarProps) {
    return <Avatar name="⚠" {...props} />;
}
